import { Box, Typography } from '@mui/material';
import type {
    CalendarBlockedDate,
    CalendarBooking,
    CalendarCleaning,
    CalendarEventType,
    MissingCleaning,
} from '../types/calendar';

interface CalendarEventProps {
    type: CalendarEventType;
    label: string;
    isStart: boolean;
    isEnd: boolean;
    isMobile: boolean;
    booking?: CalendarBooking;
    blockedDate?: CalendarBlockedDate;
    cleaning?: CalendarCleaning;
    missingCleaning?: MissingCleaning;
}

const getEventColors = (type: CalendarEventType) => {
    switch (type) {
        case 'booking':
            return {
                background: 'rgba(99, 102, 241, 0.3)',
                border: '#6366f1',
                text: '#c7d2fe',
                dashed: false,
            };
        case 'blocked-maintenance':
            return {
                background: 'rgba(239, 68, 68, 0.3)',
                border: '#ef4444',
                text: '#fecaca',
                dashed: false,
            };
        case 'blocked-personal':
            return {
                background: 'rgba(245, 158, 11, 0.3)',
                border: '#f59e0b',
                text: '#fde68a',
                dashed: false,
            };
        case 'cleaning':
            return {
                background: 'rgba(34, 197, 94, 0.3)',
                border: '#22c55e',
                text: '#bbf7d0',
                dashed: false,
            };
        case 'cleaning-unpaid':
            return {
                background: 'rgba(245, 158, 11, 0.3)',
                border: '#f59e0b',
                text: '#fde68a',
                dashed: false,
            };
        case 'missing-cleaning':
            return {
                background: 'rgba(245, 158, 11, 0.15)',
                border: '#f59e0b',
                text: '#fde68a',
                dashed: true,
            };
    }
};

const CalendarEvent = ({
    type,
    label,
    isStart,
    isEnd,
    isMobile,
    booking,
}: CalendarEventProps) => {
    const colors = getEventColors(type);

    const borderStyle = colors.dashed ? 'dashed' : 'solid';

    // On mobile, show contained event bars without negative margins
    if (isMobile && !isStart) {
        return (
            <Box
                sx={{
                    height: '18px',
                    backgroundColor: colors.background,
                    borderTop: `2px ${borderStyle} ${colors.border}`,
                    borderBottom: `2px ${borderStyle} ${colors.border}`,
                    borderRight: isEnd
                        ? `2px ${borderStyle} ${colors.border}`
                        : 'none',
                    mx: 0.25,
                    cursor: 'pointer',
                }}
            />
        );
    }

    return (
        <Box
            sx={{
                height: isMobile ? '18px' : '22px',
                backgroundColor: colors.background,
                borderTop: `2px ${borderStyle} ${colors.border}`,
                borderBottom: `2px ${borderStyle} ${colors.border}`,
                borderLeft: isStart
                    ? `2px ${borderStyle} ${colors.border}`
                    : 'none',
                borderRight: isEnd
                    ? `2px ${borderStyle} ${colors.border}`
                    : 'none',
                // On mobile, use contained margins; on desktop, use negative margins for spanning effect
                marginLeft: isMobile ? 0.25 : isStart ? '2px' : '-1px',
                marginRight: isMobile ? 0.25 : isEnd ? '2px' : '-1px',
                cursor: 'pointer',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                transition: 'all 0.15s ease',
                '&:hover': {
                    backgroundColor: colors.border,
                    transform: 'scaleY(1.1)',
                },
            }}
        >
            {isStart && (
                <>
                    {type === 'booking' && booking?.countryCode && (
                        <img
                            src={`/flags/${booking.countryCode.toLowerCase()}.png`}
                            alt={`${booking.countryCode} flag`}
                            style={{
                                width: isMobile ? 14 : 16,
                                height: isMobile ? 10 : 11,
                                objectFit: 'cover',
                                marginLeft: 4,
                                marginRight: 2,
                                flexShrink: 0,
                            }}
                        />
                    )}
                    <Typography
                        sx={{
                            fontSize: isMobile ? '0.6rem' : '0.7rem',
                            fontWeight: 700,
                            color: colors.text,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            px: 0.5,
                            textTransform: 'uppercase',
                            letterSpacing: '0.02em',
                        }}
                    >
                        {label}
                    </Typography>
                </>
            )}
        </Box>
    );
};

export default CalendarEvent;
